/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Banknote, Copy, AlertCircle, Receipt, FileText, Download, HelpCircle } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const TYPE_LABEL = {
    gst: "GST Return",
    income_tax: "Income Tax Return",
    provisional: "Provisional Tax",
    paye: "PAYE Filing",
    fbt: "FBT Return",
};
const STATUS_LABEL = {
    drafting: "Drafting",
    awaiting_info: "Awaiting your info",
    ready_for_review: "Ready for your review",
    filed: "Filed with IRD",
    paid: "Paid",
    refunded: "Refunded",
};
const STATUS_TONE = {
    drafting: "bg-zinc-100 text-zinc-700 border-zinc-200",
    awaiting_info: "bg-amber-50 text-amber-800 border-amber-200",
    ready_for_review: "bg-sky-50 text-sky-800 border-sky-200",
    filed: "bg-violet-50 text-violet-800 border-violet-200",
    paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
    refunded: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

// IRD's official "Code" used in NZ online-banking payment for each tax type.
// (Particulars = IRD number, Reference = period end.)
const IRD_CODE = {
    gst: "GST",
    income_tax: "INC",
    provisional: "INC",
    paye: "DED",
    fbt: "FBT",
};

function daysUntil(date) {
    if (!date) return null;
    const d = new Date(date + "T00:00:00").getTime();
    return Math.round((d - Date.now()) / (1000 * 60 * 60 * 24));
}
function fmtDate(d) {
    if (!d) return "—";
    return new Date(d + "T00:00:00").toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
}

export default function PortalReturns() {
    const { activeClient, activeClientId } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeRow, setActiveRow] = useState(null);

    async function load() {
        if (!activeClientId) return;
        setLoading(true);
        try {
            const { data } = await api.get("/tax-returns/", { params: { client_id: activeClientId } });
            setItems(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [activeClientId]);

    const { needsAttention, recent } = useMemo(() => {
        const needs = items.filter((r) => r.status === "ready_for_review" || r.status === "awaiting_info" || (r.status === "filed" && !r.paid_at));
        const rec = items.filter((r) => !needs.includes(r));
        return { needsAttention: needs, recent: rec };
    }, [items]);

    return (
        <div data-testid="portal-returns-page">
            <PageHeader
                eyebrow={activeClient?.name || "Returns"}
                title="Your tax returns"
                description="Every GST, Income Tax, PAYE and provisional return your accountant has on the go for you — with the period, due date and amount ready to copy when you pay."
                testId="portal-returns-header"
            />

            <PaymentGuideBanner />

            {loading ? (
                <div className="flex items-center justify-center py-16 text-[#5E636E]"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : items.length === 0 ? (
                <EmptyState icon={Receipt} title="Nothing here yet" description="When your accountant adds a tax return for you, it'll appear here." testId="portal-returns-empty" />
            ) : (
                <div className="space-y-6">
                    {needsAttention.length > 0 && (
                        <section data-testid="portal-returns-attention">
                            <h2 className="font-display text-lg mb-2 inline-flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-amber-600" /> Needs your attention
                            </h2>
                            <div className="space-y-2">
                                {needsAttention.map((r) => <ReturnCard key={r.id} row={r} onClick={() => setActiveRow(r)} highlight />)}
                            </div>
                        </section>
                    )}
                    {recent.length > 0 && (
                        <section data-testid="portal-returns-history">
                            <h2 className="font-display text-lg mb-2">History</h2>
                            <div className="space-y-2">
                                {recent.map((r) => <ReturnCard key={r.id} row={r} onClick={() => setActiveRow(r)} />)}
                            </div>
                        </section>
                    )}
                </div>
            )}

            {activeRow && (
                <ReturnDialog row={activeRow} onClose={() => setActiveRow(null)} onChanged={() => { setActiveRow(null); load(); }} />
            )}
        </div>
    );
}

function ReturnCard({ row, onClick, highlight }) {
    const dleft = daysUntil(row.due_date);
    const overdue = dleft < 0 && !["paid", "refunded"].includes(row.status);
    const dueSoon = !overdue && dleft <= 14 && !["paid", "refunded"].includes(row.status);
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full text-left rounded-xl border bg-white px-4 py-4 transition-all hover:shadow-md hover:border-[#0E3B2E]/40 ${highlight ? "border-[#0E3B2E]/40 ring-1 ring-[#0E3B2E]/10" : "border-[#E4E4E7]"}`}
            data-testid={`portal-return-card-${row.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-medium text-base">{TYPE_LABEL[row.tax_type]}</div>
                    <div className="mt-0.5 text-xs text-[#5E636E]">Period ending <span className="text-[#0F1115] font-medium">{fmtDate(row.period_end)}</span></div>
                </div>
                <span className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_TONE[row.status]}`} data-testid={`portal-return-status-${row.id}`}>
                    {STATUS_LABEL[row.status]}
                </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#5E636E]">Amount</div>
                    <div className="mt-0.5 text-[#0F1115] font-medium tabular-nums">{typeof row.amount === "number" ? `$${row.amount.toLocaleString("en-NZ", { minimumFractionDigits: 2 })}` : "—"}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#5E636E]">Due</div>
                    <div className={`mt-0.5 font-medium tabular-nums ${overdue ? "text-rose-700" : dueSoon ? "text-amber-700" : "text-[#0F1115]"}`}>
                        {fmtDate(row.due_date)}
                        {overdue && <span className="ml-1 text-[10px]">· {Math.abs(dleft)}d late</span>}
                        {dueSoon && <span className="ml-1 text-[10px]">· in {dleft}d</span>}
                    </div>
                </div>
            </div>
        </button>
    );
}

function CopyField({ label, value, testId }) {
    async function copy() {
        try { await navigator.clipboard.writeText(String(value)); toast.success(`${label} copied`); }
        catch { /* ignore */ }
    }
    return (
        <button type="button" onClick={copy} className="group w-full text-left rounded-md border border-[#E4E4E7] bg-[#FAFAFA] hover:bg-white px-3 py-2.5 transition-colors" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#5E636E]">{label}</div>
            <div className="mt-0.5 flex items-center justify-between gap-2">
                <div className="text-sm font-medium tabular-nums truncate">{value}</div>
                <Copy className="h-3.5 w-3.5 text-[#5E636E] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </button>
    );
}

function ReturnDialog({ row, onClose, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [bankRef, setBankRef] = useState("");
    const [showPay, setShowPay] = useState(false);

    async function acknowledge() {
        setBusy(true);
        try {
            await api.post(`/tax-returns/${row.id}/acknowledge`);
            toast.success("Thanks — your accountant has been notified.");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function markPaid() {
        setBusy(true);
        try {
            await api.post(`/tax-returns/${row.id}/mark-paid`, { bank_reference: bankRef || null });
            toast.success("Marked as paid — well done!");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function downloadPdf() {
        try {
            const res = await api.get(`/tax-returns/${row.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = row.pdf_file_name || "ird-return.pdf";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    const canAcknowledge = row.status === "ready_for_review" && !row.client_acknowledged_at;
    const canMarkPaid = (row.status === "filed" || row.status === "ready_for_review") && !row.paid_at;
    const amountStr = typeof row.amount === "number" ? row.amount.toFixed(2) : "—";
    const irdCode = IRD_CODE[row.tax_type] || "";

    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="portal-return-dialog">
                <DialogHeader>
                    <DialogTitle>{TYPE_LABEL[row.tax_type]}</DialogTitle>
                    <DialogDescription>Period ending {fmtDate(row.period_end)} · {STATUS_LABEL[row.status]}</DialogDescription>
                </DialogHeader>

                {row.pdf_file_name && (
                    <button type="button" onClick={downloadPdf} className="flex items-center justify-between gap-2 rounded-md border border-[#0E3B2E]/30 bg-[#0E3B2E]/5 px-3 py-2 hover:bg-[#0E3B2E]/10" data-testid="portal-pdf-download">
                        <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-4 w-4 text-[#0E3B2E] shrink-0" />
                            <div className="text-left min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.12em] text-[#5E636E]">IRD return document</div>
                                <div className="text-sm font-medium text-[#0F1115] truncate">{row.pdf_file_name}</div>
                            </div>
                        </div>
                        <Download className="h-4 w-4 text-[#0E3B2E] shrink-0" />
                    </button>
                )}

                <div className="co-label mt-2 pt-2 border-t border-[#E4E4E7]">Pay via online banking — copy these into your bank's IRD payment screen</div>
                <div className="space-y-2">
                    <CopyField label="Amount" value={`$${amountStr}`} testId="copy-amount" />
                    <CopyField label="Tax type / Code" value={irdCode || TYPE_LABEL[row.tax_type]} testId="copy-tax-code" />
                    <CopyField label="Reference (period end)" value={fmtDate(row.period_end)} testId="copy-period" />
                    <CopyField label="Due date" value={fmtDate(row.due_date)} testId="copy-due" />
                    {row.ird_reference && <CopyField label="IRD reference" value={row.ird_reference} testId="copy-ird-ref" />}
                </div>

                <p className="mt-2 text-[11px] text-[#5E636E] leading-relaxed">
                    In your bank, select <span className="font-medium text-[#0F1115]">Inland Revenue</span> from the bill-payee list — you won&apos;t need IRD&apos;s account number. Enter your IRD number in <span className="font-medium text-[#0F1115]">Particulars</span> and the tax code above in <span className="font-medium text-[#0F1115]">Code</span>.
                </p>

                {row.notes && (
                    <div className="mt-3 rounded-md bg-[#F5F4EF] border border-[#E4E4E7] p-3 text-xs text-[#0F1115] leading-relaxed">
                        <div className="co-label mb-1">Note from your accountant</div>
                        {row.notes}
                    </div>
                )}

                {row.paid_at && (
                    <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 inline-flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5" /> Paid {fmtDate(row.paid_at.slice(0, 10))}{row.paid_bank_reference ? ` · ref ${row.paid_bank_reference}` : ""}</div>
                )}

                {showPay && canMarkPaid && (
                    <div className="mt-3 space-y-2 rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3">
                        <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Bank reference (optional)</Label>
                        <Input value={bankRef} onChange={(e) => setBankRef(e.target.value)} placeholder="e.g. last 4 of payment ref" data-testid="portal-paid-ref" />
                    </div>
                )}

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    {canAcknowledge && (
                        <Button onClick={acknowledge} disabled={busy} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="portal-acknowledge">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" /> I&apos;ve reviewed this</>}
                        </Button>
                    )}
                    {canMarkPaid && (
                        showPay ? (
                            <Button onClick={markPaid} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="portal-mark-paid-confirm">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm — mark as paid"}
                            </Button>
                        ) : (
                            <Button onClick={() => setShowPay(true)} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="portal-mark-paid">
                                <Banknote className="h-4 w-4" /> Mark as paid
                            </Button>
                        )
                    )}
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PaymentGuideBanner() {
    const [open, setOpen] = useState(false);
    return (
        <div className="mb-5 rounded-xl border border-[#0E3B2E]/20 bg-[#0E3B2E]/[0.04] p-4" data-testid="portal-payment-guide">
            <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start justify-between gap-3 text-left">
                <div className="flex gap-2.5">
                    <HelpCircle className="h-4 w-4 text-[#0E3B2E] mt-0.5 shrink-0" />
                    <div>
                        <div className="font-medium text-sm text-[#0F1115]">How to pay your IRD return online</div>
                        <div className="text-xs text-[#5E636E] mt-0.5">Tap any return below to copy the amount, tax code and period — then paste into your bank.</div>
                    </div>
                </div>
                <span className="text-xs text-[#0E3B2E] font-medium shrink-0">{open ? "Hide" : "Show"}</span>
            </button>
            {open && (
                <ol className="mt-3 ml-7 list-decimal space-y-1.5 text-xs text-[#0F1115] leading-relaxed">
                    <li>Log in to your <span className="font-medium">NZ online banking</span> (ANZ, ASB, BNZ, Westpac, Kiwibank — all support IRD).</li>
                    <li>Pick <span className="font-medium">&quot;Pay tax / IRD&quot;</span> or select <span className="font-medium">Inland Revenue</span> from your saved/bill-payee list. You won&apos;t need IRD&apos;s account number.</li>
                    <li><span className="font-medium">Particulars</span>: your IRD number. <span className="font-medium">Code</span>: the tax code (GST / INC / DED / FBT). <span className="font-medium">Reference</span>: the period end shown on the return.</li>
                    <li>Enter the <span className="font-medium">amount</span> shown on the return.</li>
                    <li>Hit pay. Then come back here and tap <span className="font-medium">Mark as paid</span> so we know it&apos;s done.</li>
                </ol>
            )}
        </div>
    );
}

